import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '../../../../../../data/supabase';
import { useAuth } from '../../../../../../shared/context/useAuth';
import moment from 'moment';
import toast from 'react-hot-toast';

// Serviço centralizado para processamento de pagamentos
import { paymentProcessingService } from '../../../../../../services/paymentProcessingService';

const Success = () => {
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useHistory();
  const location = useLocation();
  const { currentUser } = useAuth();

useEffect(() => {
  const processOrder = async () => {
    try {
      setLoading(true);
      
      // Extrair session_id da URL
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');
      
      if (!sessionId) {
        throw new Error('ID da sessão não encontrado');
      }
      
      // Obter detalhes da sessão do Stripe
      const response = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/payments/order/${sessionId}`,
        { timeout: 10000 }
      );
      
      if (!response.data) {
        throw new Error('Dados da sessão não encontrados');
      }
      
      setOrderDetails(response.data);
      
      // Obter dados do paciente
      const { data: patient, error: patientError } = await supabase
        .from('patient')
        .select('id')
        .eq('id_user', currentUser.id)
        .single();
      
      if (patientError || !patient) {
        throw new Error('Paciente não encontrado');
      }
      
      // Verificar se é um pacote ou sessão individual usando metadados
      // Garantir que a verificação seja feita corretamente
      // const isPackage = response.data.session_details?.metadata?.isPackage === "true";
      const isPackage = response.data?.product_details?.name.includes("Pacote")
      
      console.log('Metadados da sessão:', response.data.session_details?.metadata);
      console.log('Processando como:', isPackage ? 'pacote' : 'crédito individual');
      
      // Usar o serviço centralizado para processar a compra
      if (isPackage) {
        await paymentProcessingService.processPackagePurchase(response.data, patient.id);
      } else {
        await paymentProcessingService.processSingleSessionPurchase(response.data, patient.id);
      }
      
      // Atualizar a UI com sucesso
      toast.success('Compra processada com sucesso!');
      
    } catch (error) {
      console.error('Erro ao processar pedido:', error);
      setError(error.message || 'Ocorreu um erro ao processar seu pedido');
      toast.error(`Erro: ${error.message || 'Ocorreu um erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };
  
  processOrder();
}, [location.search, currentUser?.id]);
  
  // Renderização condicional baseada no estado de loading e error
  if (loading) {
    return (
      <div className="container mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-4">Processando seu pedido...</h1>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BBED0] mx-auto"></div>
        <p className="mt-4 text-gray-600">Isso pode levar alguns instantes. Por favor, não feche esta página.</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-4 text-red-500">Erro no Processamento</h1>
        <p className="mb-4">{error}</p>
        <p className="mb-6 text-gray-600">
          Não se preocupe, se o valor foi debitado, entraremos em contato para resolver.
          Você também pode entrar em contato com nosso suporte.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <button
            onClick={() => navigate.push('/pagamentos')}
            className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
          >
            Voltar para Pagamentos
          </button>
          <button
            onClick={() => window.location.href = 'mailto:suporte@seuapp.com.br?subject=Problema%20com%20pagamento'}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Contatar Suporte
          </button>
                  </div>
      </div>
    );
  }
  
  if (!orderDetails) {
    return (
      <div className="container mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-4 text-red-500">Informações do Pedido não Encontradas</h1>
        <p className="mb-4">Não foi possível recuperar os detalhes do seu pedido.</p>
        <button
          onClick={() => navigate.push('/pagamentos')}
          className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
        >
          Voltar para Pagamentos
        </button>
      </div>
    );
  }
  
  // Verificar se é um pacote ou sessão individual de forma segura
  // const isPackage = orderDetails.session_details?.metadata?.isPackage === "true";
  const isPackage = orderDetails.data?.product_details?.name.includes("Pacote")

  const sessionType = orderDetails.session_details?.metadata?.sessionType || 'individual';
  
  // Extrair informações do pacote de forma segura
  let packageInfo = { sessions: 1, validity: 30 };
  if (isPackage && orderDetails.session_details?.metadata?.packageInfo) {
    try {
      packageInfo = JSON.parse(orderDetails.session_details.metadata.packageInfo);
    } catch (e) {
      console.error('Erro ao analisar packageInfo:', e);
    }
  }
  
  return (
    <div className="container mx-auto p-6">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-6">
          <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <h1 className="text-2xl font-bold mt-4">Compra Realizada com Sucesso!</h1>
          <p className="text-gray-600 mt-2">
            {isPackage 
              ? "Seu pacote de terapia foi ativado com sucesso."
              : "Seu crédito para sessão foi adicionado com sucesso."}
          </p>
        </div>
        
        <div className="border-t border-b border-gray-200 py-4 my-4">
          <h2 className="text-lg font-semibold mb-3">Detalhes do Pedido</h2>
          <div className="space-y-2">
            <p>
              <span className="font-medium">Produto:</span> {orderDetails.product_details.name}
            </p>
            <p>
              <span className="font-medium">Tipo:</span> {
                sessionType === 'individual' ? 'Individual' :
                sessionType === 'casal' ? 'Casal' :
                sessionType === 'familia' ? 'Família' : 'Individual'
              }
            </p>
            
            {isPackage && (
              <>
                <p>
                  <span className="font-medium">Sessões:</span> {packageInfo.sessions || 1}
                </p>
                <p>
                  <span className="font-medium">Validade:</span> {
                    moment().add(packageInfo.validity || 30, 'days').format('DD/MM/YYYY')
                  }
                </p>
              </>
            )}
            
            <p>
              <span className="font-medium">Data da compra:</span> {
                moment(orderDetails.session_details.created).format('DD/MM/YYYY HH:mm')
              }
            </p>
            <p>
              <span className="font-medium">Valor total:</span> {
                new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(orderDetails.session_details.amount_total / 100)
              }
            </p>
            
            <p className="text-sm text-gray-500 mt-2">
              ID da transação: {orderDetails.session_details.id.substring(0, 8)}...
            </p>
          </div>
        </div>
        
        <div className="text-center mt-6 space-y-3">
          <p>Você já pode agendar {isPackage ? "suas sessões" : "sua sessão"} de terapia!</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => navigate.push('/consultas')}
              className="px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
            >
              Agendar Sessão
            </button>
            <button
              onClick={() => navigate.push('/historico')}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Ver Meus Créditos e Pacotes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Success;